import { getRuntime, runtimeKeys } from '@codametrix/client-runtime';

const BFF_PREFIX = '/api/bff';
const PUBLIC_BFF_PREFIX = '/api/bff/public';

/** Pre-auth paths with non-default public BFF URLs. */
const PUBLIC_BFF_PATH_OVERRIDES: Record<string, string> = {
  '/user/config/global/v1': `${PUBLIC_BFF_PREFIX}/user/config/v1`
};

/** Pre-auth paths routed through public BFF without JWT (api-proxy permitAll). */
const PUBLIC_BFF_PREFIXES = [
  '/login',
  '/logout',
  '/exchangeToken',
  '/exchangeTokenSso',
  '/j_spring_security_check',
  '/users/resetPassword/v1',
  '/users/savePassword/v1',
  '/organization/by-code'
];

const UNCHANGED_PREFIXES = [
  '/api/bff',
  '/api/service-desk/bff',
  '/actuator',
  '/health',
  '/track',
  '/engage',
  '/sdk/'
];

type BffRoutingState = {
  runtimeApiProxyRoutesEnabled: boolean | undefined;
};

const BFF_ROUTING_GLOBAL_KEY = '__cmx_bffRouting';

/** Shared across duplicate ui-common evaluations (host bundle + dist chunk + remotes). */
function getBffRoutingState(): BffRoutingState {
  const g = globalThis as typeof globalThis & {
    [BFF_ROUTING_GLOBAL_KEY]?: BffRoutingState;
  };
  if (!g[BFF_ROUTING_GLOBAL_KEY]) {
    g[BFF_ROUTING_GLOBAL_KEY] = { runtimeApiProxyRoutesEnabled: undefined };
  }
  return g[BFF_ROUTING_GLOBAL_KEY];
}

export function setRuntimeApiProxyRoutesEnabled(enabled: boolean | undefined): void {
  getBffRoutingState().runtimeApiProxyRoutesEnabled = enabled;
}

function matchesPrefix(path: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => {
    if (path === prefix) {
      return true;
    }
    const normalized = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
    return path === normalized || path.startsWith(`${normalized}/`);
  });
}

function isAbsoluteUrl(path: string): boolean {
  return /^https?:\/\//i.test(path);
}

/** Split path and query so BFF rewriting applies to the pathname only. */
function splitPathAndQuery(path: string): { pathname: string; query: string } {
  const queryIndex = path.indexOf('?');
  if (queryIndex === -1) {
    return { pathname: path, query: '' };
  }
  return {
    pathname: path.slice(0, queryIndex),
    query: path.slice(queryIndex)
  };
}

/**
 * Prefix a CMx path for the BFF. Paths that already start with `/api/` (e.g.
 * `/api/cases/...`) drop that segment so the result is `/api/bff/cases/...`,
 * not `/api/bff/api/cases/...`.
 */
function prependBffPrefix(path: string): string {
  const { pathname, query } = splitPathAndQuery(path);
  const pathForBff = pathname.startsWith('/api/') ? pathname.slice(4) : pathname;
  return `${BFF_PREFIX}${pathForBff}${query}`;
}

function resolvePublicBffPath(path: string): string | undefined {
  const queryIndex = path.indexOf('?');
  const pathname = queryIndex === -1 ? path : path.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : path.slice(queryIndex);
  const override = PUBLIC_BFF_PATH_OVERRIDES[pathname];
  if (override) {
    return `${override}${query}`;
  }
  if (matchesPrefix(pathname, PUBLIC_BFF_PREFIXES)) {
    return `${PUBLIC_BFF_PREFIX}${pathname}${query}`;
  }
  return undefined;
}

export function isApiProxyRoutesEnabled(): boolean {
  const runtimeFlag = getRuntime().flag(runtimeKeys.USE_API_PROXY_ROUTES);
  if (runtimeFlag !== undefined) {
    return runtimeFlag;
  }

  const { runtimeApiProxyRoutesEnabled } = getBffRoutingState();
  if (runtimeApiProxyRoutesEnabled === true) {
    return true;
  }
  if (runtimeApiProxyRoutesEnabled === false) {
    return false;
  }

  return false;
}

export function resolveApiPath(path: string): string {
  if (!path || isAbsoluteUrl(path)) {
    return path;
  }

  if (path.startsWith('/api/bff')) {
    return path;
  }

  if (matchesPrefix(path, UNCHANGED_PREFIXES)) {
    return path;
  }

  if (!isApiProxyRoutesEnabled()) {
    return path;
  }

  const publicBffPath = resolvePublicBffPath(path);
  if (publicBffPath) {
    return publicBffPath;
  }

  return prependBffPrefix(path);
}

export function resolveRequestInfo(input: RequestInfo): RequestInfo {
  if (typeof input === 'string') {
    return resolveApiPath(input);
  }

  if (input instanceof URL) {
    if (input.origin === window.location.origin) {
      const resolvedPath = resolveApiPath(`${input.pathname}${input.search}`);
      return new URL(resolvedPath, window.location.origin).toString();
    }
    return input;
  }

  const url = input.url;
  if (isAbsoluteUrl(url)) {
    try {
      const parsed = new URL(url);
      if (parsed.origin === window.location.origin) {
        const resolvedPath = resolveApiPath(`${parsed.pathname}${parsed.search}`);
        return new Request(new URL(resolvedPath, window.location.origin).toString(), input);
      }
    } catch {
      return input;
    }
    return input;
  }

  return new Request(resolveApiPath(url), input);
}
